import { Link, useLocation } from "wouter";
import {
  Boxes,
  ChevronLeft,
  ChevronRight,
  Coins,
  Database,
  Gem,
  Handshake,
  KeyRound,
  Layers3,
  Network,
  ShieldCheck,
  Shield,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";
import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const nav = [
  { href: "/tweeto-admin/oura/protocol", label: "Protocol", icon: Shield },
  { href: "/tweeto-admin/oura/projects", label: "Projects", icon: Database },
  { href: "/tweeto-admin/oura/nfts", label: "NFTs", icon: Gem },
  { href: "/tweeto-admin/oura/traits", label: "Traits", icon: Layers3 },
  { href: "/tweeto-admin/oura/tokens", label: "Tokens", icon: Coins },
  { href: "/tweeto-admin/oura/featured-partners", label: "Featured Partners", icon: Handshake },
  { href: "/tweeto-admin/oura/vault", label: "Vault", icon: Wallet },
  { href: "/tweeto-admin/oura/history", label: "History", icon: Boxes },
  { href: "/tweeto-admin/partners", label: "Partners", icon: Network },
];

const rbacNav = [
  { href: "/tweeto-admin/rbac/users", label: "Users & Roles", icon: Users },
  { href: "/tweeto-admin/rbac/roles", label: "Roles & Permissions", icon: ShieldCheck },
  { href: "/tweeto-admin/rbac/partners", label: "Partner Admins", icon: UserCog },
  { href: "/tweeto-admin/rbac/whitelist", label: "Role Whitelist", icon: KeyRound },
];

export function TweetoAdminLayout({ children, username }: { children: ReactNode; username: string }) {
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const sectionTitle = location.startsWith("/tweeto-admin/rbac")
    ? "RBAC Management"
    : location.startsWith("/tweeto-admin/partners")
      ? "Partner Management"
      : "Oura Management";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-20 border-r border-zinc-800 bg-zinc-950 transition-all",
          collapsed ? "w-16" : "w-64",
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-zinc-800 px-4">
          {!collapsed && (
            <div>
              <div className="text-sm font-semibold">Tweeto Admin</div>
              <div className="text-xs text-zinc-500">Oura operations</div>
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={() => setCollapsed((value) => !value)}>
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
        <nav className="space-y-1 px-2 py-4">
          {!collapsed && <div className="px-3 pb-2 text-xs font-medium uppercase text-zinc-600">Oura Management</div>}
          {nav.map((item) => {
            const Icon = item.icon;
            const active = location === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex h-10 items-center gap-3 rounded px-3 text-sm text-zinc-400 hover:bg-zinc-900 hover:text-white",
                  (active || location.startsWith(`${item.href}/`)) && "bg-zinc-800 text-white",
                  collapsed && "justify-center px-0",
                )}
                title={item.label}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!collapsed && item.label}
              </Link>
            );
          })}
          <div className="pt-4">
            {!collapsed && <div className="px-3 pb-2 text-xs font-medium uppercase text-zinc-600">RBAC Management</div>}
            {rbacNav.map((item) => {
              const Icon = item.icon;
              const active = location === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex h-10 items-center gap-3 rounded px-3 text-sm text-zinc-400 hover:bg-zinc-900 hover:text-white",
                    (active || location.startsWith(`${item.href}/`)) && "bg-zinc-800 text-white",
                    collapsed && "justify-center px-0",
                  )}
                  title={item.label}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {!collapsed && item.label}
                </Link>
              );
            })}
          </div>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-zinc-800 p-3">
          <div className={cn("flex items-center gap-3", collapsed && "justify-center")}>
            <div className="flex h-8 w-8 items-center justify-center rounded bg-zinc-800">
              <KeyRound className="h-4 w-4" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="truncate text-sm">{username}</div>
                <div className="text-xs text-zinc-500">system_admin</div>
              </div>
            )}
          </div>
        </div>
      </aside>
      <main className={cn("min-h-screen transition-all", collapsed ? "pl-16" : "pl-64")}>
        <header className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-950/90 px-6 py-4 backdrop-blur">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-zinc-500">Tweeto Admin</div>
              <h1 className="text-xl font-semibold">{sectionTitle}</h1>
            </div>
            <div className="flex items-center gap-2 rounded border border-zinc-800 px-3 py-2 text-sm text-zinc-300">
              <Shield className="h-4 w-4" />
              Admin only
            </div>
          </div>
        </header>
        <div className="mx-auto max-w-[1400px] px-6 py-6">{children}</div>
      </main>
    </div>
  );
}
